import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Users, DollarSign, TrendingUp, CreditCard, 
  LogOut, RefreshCw, Loader2, UserX, Clock, Activity,
  ChevronRight, AlertCircle, Gift, CalendarPlus, Crown,
  Megaphone, Download, FileText, Mail, MessageSquare,
  Plus, X, Send, Copy, Check, Trash2, Heart, HeartPulse,
  AlertTriangle, TrendingDown, Filter, KeyRound
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [adminUser, setAdminUser] = useState(null);
  
  // New states for features
  const [announcements, setAnnouncements] = useState([]);
  const [failedPayments, setFailedPayments] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '', type: 'info' });
  const [showNoteModal, setShowNoteModal] = useState(null); // userId
  const [newNote, setNewNote] = useState('');
  const [userNotes, setUserNotes] = useState({});
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [trialExtensionDays, setTrialExtensionDays] = useState({});
  
  // Health score states
  const [healthSummary, setHealthSummary] = useState(null);
  const [healthFilter, setHealthFilter] = useState('');
  const [showHealthDetails, setShowHealthDetails] = useState(null);
  
  // Password reset states
  const [showPasswordReset, setShowPasswordReset] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  
  // Grant premium duration states
  const [premiumDuration, setPremiumDuration] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    setAdminUser(JSON.parse(user));
    loadDashboardData();
  }, [navigate]);

  const getAdminApi = () => {
    const token = localStorage.getItem('admin_token');
    return {
      get: (url) => api.get(url, { headers: { Authorization: `Bearer ${token}` } }),
      post: (url, data) => api.post(url, data, { headers: { Authorization: `Bearer ${token}` } }),
      put: (url, data) => api.put(url, data, { headers: { Authorization: `Bearer ${token}` } }),
      delete: (url) => api.delete(url, { headers: { Authorization: `Bearer ${token}` } })
    };
  };

  const loadDashboardData = async () => {
    setLoading(true);
    const adminApi = getAdminApi();
    
    try {
      const [statsRes, usersRes, transactionsRes, announcementsRes, failedRes, healthRes] = await Promise.all([
        adminApi.get('/admin/dashboard/stats'),
        adminApi.get(`/admin/users?limit=50${healthFilter ? `&health_filter=${healthFilter}` : ''}`),
        adminApi.get('/admin/transactions?limit=10'),
        adminApi.get('/admin/announcements'),
        adminApi.get('/admin/failed-payments'),
        adminApi.get('/admin/health-summary')
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTransactions(transactionsRes.data.transactions);
      setAnnouncements(announcementsRes.data.announcements || []);
      setFailedPayments(failedRes.data.failedPayments || []);
      setHealthSummary(healthRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (userId, userEmail) => {
    if (!window.confirm(`Cancel subscription for ${userEmail}?`)) return;
    
    const adminApi = getAdminApi();
    try {
      await adminApi.post(`/admin/users/${userId}/cancel-subscription`);
      toast.success('Subscription cancelled');
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const handleExtendTrial = async (userId, userEmail, days = 7) => {
    if (!window.confirm(`Extend trial by ${days} days for ${userEmail}?`)) return;
    
    const adminApi = getAdminApi();
    try {
      const response = await adminApi.post(`/admin/users/${userId}/extend-trial-custom`, { days });
      toast.success(response.data.message);
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend trial');
    }
  };

  const handleGrantPremium = async (userId, userEmail, months = 12) => {
    const label = months === 1 ? '1 month' : months === 3 ? '3 months' : months === 6 ? '6 months' : '1 year';
    if (!window.confirm(`Grant FREE Premium access (${label}) to ${userEmail}?`)) return;
    
    const adminApi = getAdminApi();
    try {
      const response = await adminApi.post(`/admin/users/${userId}/grant-premium`, { months });
      toast.success(response.data.message);
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant premium');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('This will generate a temporary password for this user. Continue?')) return;
    
    setResetPasswordLoading(true);
    const adminApi = getAdminApi();
    try {
      const response = await adminApi.post(`/admin/users/${userId}/reset-password`);
      setShowPasswordReset(response.data);
      toast.success('Temporary password generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Announcement handlers
  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error('Please fill in title and message');
      return;
    }
    
    const adminApi = getAdminApi();
    try {
      await adminApi.post('/admin/announcements', newAnnouncement);
      toast.success('Announcement created');
      setShowAnnouncementModal(false);
      setNewAnnouncement({ title: '', message: '', type: 'info' });
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Delete this announcement?')) return;
    
    const adminApi = getAdminApi();
    try {
      await adminApi.delete(`/admin/announcements/${announcementId}`);
      toast.success('Announcement deleted');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleToggleAnnouncement = async (announcement) => {
    const adminApi = getAdminApi();
    try {
      await adminApi.put(`/admin/announcements/${announcement.id}`, {
        ...announcement,
        active: !announcement.active
      });
      toast.success(`Announcement ${announcement.active ? 'deactivated' : 'activated'}`);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  // Export handlers
  const handleExport = async (type, format) => {
    const adminApi = getAdminApi();
    try {
      const response = await adminApi.get(`/admin/export/${type}?format=${format}`);
      
      // Download CSV
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${type} report downloaded`);
    } catch (error) {
      toast.error(`Failed to export ${type} report`);
    }
  };

  // Failed payment handlers
  const handleRetryPayment = async (paymentId) => {
    const adminApi = getAdminApi();
    try {
      await adminApi.post(`/admin/failed-payments/${paymentId}/retry`);
      toast.success('Payment marked as retry attempted');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to retry payment');
    }
  };

  const handleGenerateReminder = async (paymentId) => {
    const adminApi = getAdminApi();
    try {
      const response = await adminApi.post(`/admin/failed-payments/${paymentId}/generate-reminder`);
      setGeneratedEmail(response.data);
      toast.success('Email content generated');
    } catch (error) {
      toast.error('Failed to generate reminder');
    }
  };

  // User notes handlers
  const handleAddNote = async (userId) => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    const adminApi = getAdminApi();
    try {
      await adminApi.post(`/admin/users/${userId}/notes`, { note: newNote });
      toast.success('Note added');
      setShowNoteModal(null);
      setNewNote('');
      loadUserNotes(userId);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const loadUserNotes = async (userId) => {
    const adminApi = getAdminApi();
    try {
      const response = await adminApi.get(`/admin/users/${userId}/notes`);
      setUserNotes(prev => ({ ...prev, [userId]: response.data.notes || [] }));
    } catch (error) {
      console.error('Failed to load notes');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const formatCurrency = (amount) => `US$${amount.toFixed(2)}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-purple-500/20 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">Habit2Goals Admin</h1>
              <p className="text-xs text-slate-400">{adminUser?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadDashboardData}
              className="text-slate-400 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-red-400"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-purple-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Users</p>
                  <p className="text-3xl font-bold text-white">{stats?.totalUsers || 0}</p>
                </div>
                <Users className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-purple-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Active Subscriptions</p>
                  <p className="text-3xl font-bold text-green-400">{stats?.activeSubscriptions || 0}</p>
                </div>
                <CreditCard className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-purple-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Active Trials</p>
                  <p className="text-3xl font-bold text-yellow-400">{stats?.activeTrials || 0}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-purple-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Failed Payments</p>
                  <p className="text-3xl font-bold text-red-400">{failedPayments.length}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-green-500/20 to-emerald-600/20 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-300">Monthly Revenue (MRR)</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(stats?.mrr || 0)}</p>
                </div>
                <DollarSign className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-600/20 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-300">Annual Revenue (ARR)</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(stats?.arr || 0)}</p>
                </div>
                <Activity className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/20 to-pink-600/20 border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-300">Total Revenue</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(stats?.totalRevenue || 0)}</p>
                </div>
                <DollarSign className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Health Summary */}
        {healthSummary && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-pink-400" />
              User Health Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-gradient-to-br from-green-500/20 to-emerald-600/20 border-green-500/30 cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => { setHealthFilter('healthy'); setActiveTab('users'); }}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-300">Healthy Users</p>
                      <p className="text-3xl font-bold text-white">{healthSummary.summary.healthy}</p>
                      <p className="text-xs text-green-400 mt-1">Score 70-100</p>
                    </div>
                    <Heart className="w-10 h-10 text-green-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-600/20 border-yellow-500/30 cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => { setHealthFilter('at_risk'); setActiveTab('users'); }}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-300">At Risk</p>
                      <p className="text-3xl font-bold text-white">{healthSummary.summary.atRisk}</p>
                      <p className="text-xs text-yellow-400 mt-1">Score 40-69</p>
                    </div>
                    <AlertTriangle className="w-10 h-10 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-red-500/20 to-rose-600/20 border-red-500/30 cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => { setHealthFilter('churning'); setActiveTab('users'); }}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-300">Churning</p>
                      <p className="text-3xl font-bold text-white">{healthSummary.summary.churning}</p>
                      <p className="text-xs text-red-400 mt-1">Score 0-39</p>
                    </div>
                    <TrendingDown className="w-10 h-10 text-red-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* At Risk Users Preview */}
            {healthSummary.atRiskUsers?.length > 0 && (
              <Card className="mt-4 bg-slate-800/50 border-yellow-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-yellow-400 text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Users Needing Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {healthSummary.atRiskUsers.slice(0, 6).map((user) => (
                      <div key={user.id} className="p-3 rounded-lg bg-slate-700/50 border border-yellow-500/20">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium text-sm">{user.name || 'Unknown'}</p>
                            <p className="text-slate-400 text-xs">{user.email}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              user.score >= 50 ? 'text-yellow-400' : 'text-orange-400'
                            }`}>{user.score}</p>
                            <p className="text-xs text-slate-500">score</p>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-slate-400">
                          <span>Last login: {user.factors?.loginRecency?.daysSinceLogin || 0}d ago</span>
                          <span className="mx-2">•</span>
                          <span>Streak: {user.factors?.streak?.currentStreak || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['overview', 'users', 'announcements', 'failed-payments', 'exports'].map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); if (tab !== 'users') setHealthFilter(''); }}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab
                  ? 'bg-purple-500 text-white'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white'
              }`}
            >
              {tab === 'failed-payments' ? 'Failed Payments' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <Card className="bg-slate-800/50 border-purple-500/20 mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Announcements
                </CardTitle>
                <Button
                  onClick={() => setShowAnnouncementModal(true)}
                  className="bg-purple-500 hover:bg-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Announcement
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No announcements yet</p>
              ) : (
                <div className="space-y-4">
                  {announcements.map((ann) => (
                    <div 
                      key={ann.id} 
                      className={`p-4 rounded-lg border ${
                        ann.active ? 'border-green-500/30 bg-green-500/10' : 'border-slate-600 bg-slate-700/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              ann.type === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                              ann.type === 'success' ? 'bg-green-500/20 text-green-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {ann.type}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              ann.active ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'
                            }`}>
                              {ann.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <h4 className="text-white font-medium">{ann.title}</h4>
                          <p className="text-slate-400 text-sm mt-1">{ann.message}</p>
                          <p className="text-slate-500 text-xs mt-2">Created: {formatDate(ann.createdAt)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleAnnouncement(ann)}
                            className={ann.active ? 'text-yellow-400' : 'text-green-400'}
                          >
                            {ann.active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAnnouncement(ann.id)}
                            className="text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Failed Payments Tab */}
        {activeTab === 'failed-payments' && (
          <Card className="bg-slate-800/50 border-purple-500/20 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Failed Payments Recovery
              </CardTitle>
            </CardHeader>
            <CardContent>
              {failedPayments.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No failed payments</p>
              ) : (
                <div className="space-y-4">
                  {failedPayments.map((payment) => (
                    <div key={payment.id} className="p-4 rounded-lg border border-red-500/30 bg-red-500/10">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{payment.userName || 'Unknown User'}</p>
                          <p className="text-slate-400 text-sm">{payment.userEmail}</p>
                          <p className="text-red-400 font-medium mt-1">{formatCurrency(payment.amount || 2.99)}</p>
                          <p className="text-slate-500 text-xs mt-1">Failed: {formatDate(payment.createdAt)}</p>
                          {payment.retryAttempted && (
                            <p className="text-yellow-400 text-xs mt-1">Retry attempted: {formatDate(payment.retryAttemptedAt)}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleRetryPayment(payment.id)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-black"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Retry
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerateReminder(payment.id)}
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Generate Email
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Generated Email Modal */}
              {generatedEmail && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                  <div className="absolute inset-0 bg-black/50" onClick={() => setGeneratedEmail(null)} />
                  <div className="relative bg-slate-800 rounded-xl p-6 max-w-2xl w-full border border-purple-500/30">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-white">Generated Email</h3>
                      <Button variant="ghost" size="sm" onClick={() => setGeneratedEmail(null)}>
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-400">To</Label>
                        <p className="text-white">{generatedEmail.userEmail}</p>
                      </div>
                      <div>
                        <Label className="text-slate-400">Subject</Label>
                        <p className="text-white">{generatedEmail.emailContent?.subject}</p>
                      </div>
                      <div>
                        <Label className="text-slate-400">Body</Label>
                        <pre className="text-slate-300 whitespace-pre-wrap bg-slate-900 p-4 rounded-lg text-sm">
                          {generatedEmail.emailContent?.body}
                        </pre>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(generatedEmail.emailContent?.body);
                            toast.success('Email content copied!');
                          }}
                          className="bg-purple-500 hover:bg-purple-600"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy to Clipboard
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Exports Tab */}
        {activeTab === 'exports' && (
          <Card className="bg-slate-800/50 border-purple-500/20 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Download className="w-5 h-5" />
                Export Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Revenue Report */}
                <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/10">
                  <div className="flex items-center gap-3 mb-4">
                    <DollarSign className="w-8 h-8 text-green-400" />
                    <div>
                      <h4 className="text-white font-medium">Revenue Report</h4>
                      <p className="text-slate-400 text-sm">Transactions & revenue data</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleExport('revenue', 'csv')}
                    className="bg-green-500 hover:bg-green-600 w-full"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
                
                {/* Users Report */}
                <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/10">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-8 h-8 text-blue-400" />
                    <div>
                      <h4 className="text-white font-medium">Users Report</h4>
                      <p className="text-slate-400 text-sm">User accounts & status</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleExport('users', 'csv')}
                    className="bg-blue-500 hover:bg-blue-600 w-full"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
                
                {/* Habits Report */}
                <div className="p-4 rounded-lg border border-purple-500/30 bg-purple-500/10">
                  <div className="flex items-center gap-3 mb-4">
                    <Activity className="w-8 h-8 text-purple-400" />
                    <div>
                      <h4 className="text-white font-medium">Habits Report</h4>
                      <p className="text-slate-400 text-sm">All habits & completions</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleExport('habits', 'csv')}
                    className="bg-purple-500 hover:bg-purple-600 w-full"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        {(activeTab === 'overview' || activeTab === 'users') && (
          <Card className="bg-slate-800/50 border-purple-500/20 mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {activeTab === 'users' ? 'All Users' : 'Recent Users'}
                </CardTitle>
                {activeTab === 'users' && (
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <select
                      value={healthFilter}
                      onChange={(e) => { setHealthFilter(e.target.value); loadDashboardData(); }}
                      className="bg-slate-700 text-white text-sm rounded px-3 py-1 border border-slate-600"
                    >
                      <option value="">All Users</option>
                      <option value="healthy">🟢 Healthy (70-100)</option>
                      <option value="at_risk">🟡 At Risk (40-69)</option>
                      <option value="churning">🔴 Churning (0-39)</option>
                    </select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">User</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Health Score</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Subscription Ends</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Notes</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white font-medium">{user.username || user.name}</p>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => setShowHealthDetails(user)}
                            className="flex items-center gap-2 hover:opacity-80"
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                              user.healthCategory === 'healthy' ? 'bg-green-500/30 text-green-400 border-2 border-green-500' :
                              user.healthCategory === 'at_risk' ? 'bg-yellow-500/30 text-yellow-400 border-2 border-yellow-500' :
                              'bg-red-500/30 text-red-400 border-2 border-red-500'
                            }`}>
                              {user.healthScore || 0}
                            </div>
                            <div className="text-left">
                              <span className={`text-xs font-medium ${
                                user.healthCategory === 'healthy' ? 'text-green-400' :
                                user.healthCategory === 'at_risk' ? 'text-yellow-400' :
                                'text-red-400'
                              }`}>
                                {user.healthCategory === 'healthy' ? '🟢 Healthy' :
                                 user.healthCategory === 'at_risk' ? '🟡 At Risk' :
                                 '🔴 Churning'}
                              </span>
                            </div>
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.subscriptionStatus === 'active' ? 'bg-green-500/20 text-green-400' :
                            user.subscriptionStatus === 'trial' ? 'bg-yellow-500/20 text-yellow-400' :
                            user.subscriptionStatus === 'expired' ? 'bg-red-500/20 text-red-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {user.subscriptionStatus}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-slate-300">
                            <p className="font-medium">
                              {formatDate(user.subscriptionEndsAt || user.trialEndsAt)}
                            </p>
                            {user.subscriptionStatus === 'trial' && user.trialEndsAt && (
                              <p className="text-xs text-yellow-400">
                                {Math.ceil((new Date(user.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowNoteModal(user.id);
                              loadUserNotes(user.id);
                            }}
                            className="text-slate-400 hover:text-white"
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {user.adminNotes?.length || 0}
                          </Button>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {/* Trial Extension Options */}
                            {(user.subscriptionStatus === 'trial' || user.subscriptionStatus === 'expired' || user.subscriptionStatus === 'none') && (
                              <div className="flex items-center gap-1">
                                <select
                                  value={trialExtensionDays[user.id] || 7}
                                  onChange={(e) => setTrialExtensionDays(prev => ({ ...prev, [user.id]: parseInt(e.target.value) }))}
                                  className="bg-slate-700 text-white text-xs rounded px-2 py-1 border border-slate-600"
                                >
                                  <option value={7}>+7 Days</option>
                                  <option value={14}>+14 Days</option>
                                  <option value={30}>+1 Month</option>
                                </select>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleExtendTrial(user.id, user.email, trialExtensionDays[user.id] || 7)}
                                  className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                                  title="Extend trial"
                                >
                                  <CalendarPlus className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                            
                            {/* Grant Premium */}
                            {user.subscriptionStatus !== 'active' && (
                              <div className="flex items-center gap-1">
                                <select
                                  value={premiumDuration[user.id] || 12}
                                  onChange={(e) => setPremiumDuration(prev => ({ ...prev, [user.id]: parseInt(e.target.value) }))}
                                  className="bg-slate-700 text-white text-xs rounded px-2 py-1 border border-slate-600"
                                  data-testid={`premium-duration-${user.id}`}
                                >
                                  <option value={1}>1 Month</option>
                                  <option value={3}>3 Months</option>
                                  <option value={6}>6 Months</option>
                                  <option value={12}>1 Year</option>
                                </select>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleGrantPremium(user.id, user.email, premiumDuration[user.id] || 12)}
                                  className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                                  title="Grant free premium access"
                                  data-testid={`grant-premium-btn-${user.id}`}
                                >
                                  <Crown className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                            
                            {/* Reset Password */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetPassword(user.id)}
                              className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                              title="Reset user password"
                              data-testid={`reset-password-btn-${user.id}`}
                              disabled={resetPasswordLoading}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            
                            {/* Cancel Subscription */}
                            {user.subscriptionStatus === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancelSubscription(user.id, user.email)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                title="Cancel subscription"
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transactions Table */}
        {activeTab === 'overview' && (
          <Card className="bg-slate-800/50 border-purple-500/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Recent Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Transaction ID</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Amount</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-slate-400">
                          No transactions yet
                        </td>
                      </tr>
                    ) : (
                      transactions.map((txn) => (
                        <tr key={txn.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-3 px-4 text-slate-300 font-mono text-sm">
                            {txn.id.slice(0, 20)}...
                          </td>
                          <td className="py-3 px-4 text-white font-medium">
                            {formatCurrency(txn.amount)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              txn.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                              txn.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              txn.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {txn.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-300">
                            {formatDate(txn.createdAt)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAnnouncementModal(false)} />
          <div className="relative bg-slate-800 rounded-xl p-6 max-w-lg w-full border border-purple-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">New Announcement</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAnnouncementModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Announcement title..."
                />
              </div>
              <div>
                <Label className="text-slate-300">Message</Label>
                <textarea
                  value={newAnnouncement.message}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg p-3 min-h-[100px]"
                  placeholder="Announcement message..."
                />
              </div>
              <div>
                <Label className="text-slate-300">Type</Label>
                <select
                  value={newAnnouncement.type}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, type: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg p-2"
                >
                  <option value="info">Info (Blue)</option>
                  <option value="warning">Warning (Yellow)</option>
                  <option value="success">Success (Green)</option>
                </select>
              </div>
              <Button
                onClick={handleCreateAnnouncement}
                className="w-full bg-purple-500 hover:bg-purple-600"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Create Announcement
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* User Notes Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowNoteModal(null)} />
          <div className="relative bg-slate-800 rounded-xl p-6 max-w-lg w-full border border-purple-500/30 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Admin Notes</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowNoteModal(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Add Note */}
            <div className="mb-4">
              <div className="flex gap-2">
                <Input
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white flex-1"
                  placeholder="Add a note..."
                  onKeyDown={(e) => e.key === 'Enter' && handleAddNote(showNoteModal)}
                />
                <Button onClick={() => handleAddNote(showNoteModal)} className="bg-purple-500 hover:bg-purple-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Notes List */}
            <div className="space-y-3">
              {(userNotes[showNoteModal] || []).length === 0 ? (
                <p className="text-center text-slate-400 py-4">No notes yet</p>
              ) : (
                (userNotes[showNoteModal] || []).map((note) => (
                  <div key={note.id} className="p-3 rounded-lg bg-slate-700/50 border border-slate-600">
                    <p className="text-white">{note.note}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-slate-400">
                        {note.addedBy} • {formatDate(note.addedAt)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Health Details Modal */}
      {showHealthDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowHealthDetails(null)} />
          <div className="relative bg-slate-800 rounded-xl p-6 max-w-lg w-full border border-purple-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <HeartPulse className="w-5 h-5 text-pink-400" />
                Health Score Details
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowHealthDetails(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-4 pb-4 border-b border-slate-700">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-2xl ${
                  showHealthDetails.healthCategory === 'healthy' ? 'bg-green-500/30 text-green-400 border-2 border-green-500' :
                  showHealthDetails.healthCategory === 'at_risk' ? 'bg-yellow-500/30 text-yellow-400 border-2 border-yellow-500' :
                  'bg-red-500/30 text-red-400 border-2 border-red-500'
                }`}>
                  {showHealthDetails.healthScore || 0}
                </div>
                <div>
                  <p className="text-white font-semibold">{showHealthDetails.name || showHealthDetails.username}</p>
                  <p className="text-slate-400 text-sm">{showHealthDetails.email}</p>
                  <p className={`text-sm font-medium mt-1 ${
                    showHealthDetails.healthCategory === 'healthy' ? 'text-green-400' :
                    showHealthDetails.healthCategory === 'at_risk' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {showHealthDetails.healthCategory === 'healthy' ? '🟢 Healthy User' :
                     showHealthDetails.healthCategory === 'at_risk' ? '🟡 At Risk - Needs Attention' :
                     '🔴 Churning - Urgent'}
                  </p>
                </div>
              </div>
              
              {/* Factors Breakdown */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-300">Score Breakdown</h4>
                
                {/* Login Recency */}
                <div className="p-3 rounded-lg bg-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-300 text-sm">Login Recency (25%)</span>
                    <span className="text-white font-medium">{showHealthDetails.healthFactors?.loginRecency?.score || 0}/100</span>
                  </div>
                  <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full" 
                      style={{ width: `${showHealthDetails.healthFactors?.loginRecency?.score || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Last login: {showHealthDetails.healthFactors?.loginRecency?.daysSinceLogin || 0} days ago
                  </p>
                </div>
                
                {/* Completion Rate */}
                <div className="p-3 rounded-lg bg-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-300 text-sm">Completion Rate (35%)</span>
                    <span className="text-white font-medium">{showHealthDetails.healthFactors?.completionRate?.score || 0}/100</span>
                  </div>
                  <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full" 
                      style={{ width: `${showHealthDetails.healthFactors?.completionRate?.score || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {showHealthDetails.healthFactors?.completionRate?.completionsLast7Days || 0} completions in last 7 days
                    ({showHealthDetails.healthFactors?.completionRate?.totalHabits || 0} habits)
                  </p>
                </div>
                
                {/* Streak */}
                <div className="p-3 rounded-lg bg-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-300 text-sm">Current Streak (20%)</span>
                    <span className="text-white font-medium">{showHealthDetails.healthFactors?.streak?.score || 0}/100</span>
                  </div>
                  <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-orange-500 rounded-full" 
                      style={{ width: `${showHealthDetails.healthFactors?.streak?.score || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {showHealthDetails.healthFactors?.streak?.currentStreak || 0} day streak
                  </p>
                </div>
                
                {/* Consistency */}
                <div className="p-3 rounded-lg bg-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-300 text-sm">Activity Consistency (20%)</span>
                    <span className="text-white font-medium">{showHealthDetails.healthFactors?.consistency?.score || 0}/100</span>
                  </div>
                  <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 rounded-full" 
                      style={{ width: `${showHealthDetails.healthFactors?.consistency?.score || 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Active on {showHealthDetails.healthFactors?.consistency?.activeDaysLast14 || 0}/14 days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPasswordReset(null)} />
          <div className="relative bg-slate-800 rounded-xl p-6 max-w-md w-full border border-orange-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-orange-400" />
                Password Reset
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPasswordReset(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <p className="text-orange-300 text-sm font-medium mb-1">Temporary password generated for:</p>
                <p className="text-white font-medium">{showPasswordReset.userName || 'User'}</p>
                <p className="text-slate-400 text-sm">{showPasswordReset.userEmail}</p>
              </div>
              
              <div>
                <Label className="text-slate-300 text-sm">Temporary Password</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    readOnly
                    value={showPasswordReset.temporaryPassword}
                    className="bg-slate-700 border-slate-600 text-white font-mono text-lg tracking-wider"
                    data-testid="temp-password-display"
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(showPasswordReset.temporaryPassword);
                      toast.success('Password copied!');
                    }}
                    className="bg-orange-500 hover:bg-orange-600 flex-shrink-0"
                    data-testid="copy-temp-password-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-slate-700/50 border border-slate-600">
                <p className="text-yellow-400 text-xs font-medium mb-1">Important:</p>
                <p className="text-slate-400 text-xs">Share this password securely with the user. They should change it after logging in.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
